import React, { useState, useEffect, useRef, useCallback } from 'react';
import classnames from 'classnames';

import useKeyboardEvent from 'hooks/useKeyboardEvent.js';
import { authCall } from 'util/rest';
import config from 'config';
import CauzeSpinner from 'components/CauzeSpinner/CauzeSpinner';
import { getAppLink } from 'util/userAgent';

async function fetchGiftDetails(giftId) {
  return authCall('GET', `${config.API_ROOTV2}/api/v1/gifts/${giftId}`);
}

async function submitThankYou(giftId, { message }) {
  return authCall(
    'POST',
    `${config.API_ROOTV2}/api/v1/gifts/${giftId}/thank`,
    { thank_you_message: message || null },
    { headers: { 'Content-Type': 'application/json' } },
  );
}

function getInitials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .map((word) => word[0])
    .join('')
    .toUpperCase()
    .slice(0, 3);
}

function Avatar({ name, avatarUrl }) {
  if (avatarUrl) {
    return <img src={avatarUrl} alt={name} style={styles.avatarImage} />;
  }

  return <div style={styles.avatarFallback}>{getInitials(name)}</div>;
}

function VideoPlayer({ videoUrl, videoPoster }) {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const handlePlay = useCallback(() => {
    if (!videoRef.current) return;
    setPlaying(true);
    videoRef.current.play().catch(() => setPlaying(false));
  }, []);

  const handleFullscreen = useCallback(() => {
    if (!videoRef.current) return;

    if (videoRef.current.webkitEnterFullscreen) {
      videoRef.current.webkitEnterFullscreen();
    } else if (videoRef.current.requestFullscreen) {
      videoRef.current.requestFullscreen();
    } else if (videoRef.current.webkitRequestFullscreen) {
      videoRef.current.webkitRequestFullscreen();
    }
  }, []);

  return (
    <div style={styles.videoContainer}>
      <video
        ref={videoRef}
        src={videoUrl}
        controls={playing}
        playsInline
        preload="metadata"
        poster={videoPoster || undefined}
        style={styles.videoElement}
      />

      {!playing && (
        <button
          onClick={handlePlay}
          style={styles.playBtn}
          aria-label="Play video"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M8 6L18 12L8 18V6Z" fill="#1A1A1A" />
          </svg>
        </button>
      )}

      {playing && (
        <button
          onClick={handleFullscreen}
          style={styles.videoExpandBtn}
          aria-label="Full screen"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#fff"
            strokeWidth="2"
            strokeLinecap="round"
          >
            <polyline points="15 3 21 3 21 9" />
            <polyline points="9 21 3 21 3 15" />
            <line x1="21" y1="3" x2="14" y2="10" />
            <line x1="3" y1="21" x2="10" y2="14" />
          </svg>
        </button>
      )}
    </div>
  );
}

function ThankYouForm({ gifterName, submitting, onSubmit }) {
  const [message, setMessage] = useState('Thanks so much for the gift!');
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const sendButtonRef = useRef(null);

  const handleDone = useCallback(() => {
    if (inputRef.current) {
      inputRef.current.blur();
    }
    setIsFocused(false);
  }, []);

  const handleFocus = useCallback(() => {
    setIsFocused(true);
    window.setTimeout(() => {
      sendButtonRef.current?.scrollIntoView({
        block: 'nearest',
        behavior: 'smooth',
      });
    }, 120);
  }, []);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        handleDone();
      }

      if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
        handleDone();
        onSubmit({ message });
      }
    },
    [handleDone, message, onSubmit],
  );

  return (
    <div style={styles.thankSection}>
      <div style={styles.thankHeaderRow}>
        <div style={styles.thankTitle}>Say thanks</div>
        {isFocused && (
          <button onClick={handleDone} style={styles.doneBtn} type="button">
            Done
          </button>
        )}
      </div>
      <textarea
        ref={inputRef}
        value={message}
        onChange={(event) => setMessage(event.target.value)}
        onFocus={handleFocus}
        onBlur={() => setIsFocused(false)}
        onKeyDown={handleKeyDown}
        placeholder={`Thanks so much for the gift, ${gifterName.split(' ')[0]}!`}
        maxLength={280}
        enterKeyHint="done"
        rows={2}
        style={styles.noteInput}
      />
      <button
        ref={sendButtonRef}
        onClick={() => onSubmit({ message })}
        disabled={submitting}
        style={{
          ...styles.primaryBtn,
          ...(submitting ? styles.primaryBtnDisabled : {}),
        }}
      >
        {submitting ? 'Sending...' : 'Send thanks'}
      </button>
    </div>
  );
}

function GiftDetailContent({ gift, giftId, onClose, onThanked }) {
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const gifterName = gift.gifterName || gift.gifter?.name || 'Someone';
  const gifterAvatar =
    gift.gifterAvatarUrl ||
    gift.gifter?.avatarUrl ||
    gift.gifter?.avatar_url ||
    null;
  const amount = gift.amount ? gift.amount / 100 : gift.amount || 0;
  const videoUrl = gift.videoUrl || null;
  const videoPoster = gift.videoPoster || null;
  const textMessage = gift.message || gift.gifterMessage || null;
  const alreadyThanked = gift.thanked || gift.thankResponse != null;

  const handleThank = async ({ message }) => {
    setSubmitting(true);
    setError(null);

    try {
      await submitThankYou(giftId, { message });
      setSent(true);
      if (onThanked) onThanked(giftId);
    } catch (err) {
      console.error('Thank-you failed:', err);
      setError("Couldn't send your thank-you. Tap to try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.content}>
      <button onClick={onClose} style={styles.closeBtn} aria-label="Close">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
        >
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>

      <div style={styles.header}>
        <Avatar name={gifterName} avatarUrl={gifterAvatar} />
        <div style={styles.headerText}>
          <div style={styles.headerLabel}>Gift from</div>
          <div style={styles.headerName}>{gifterName}</div>
        </div>
      </div>

      <div style={styles.mainRow}>
        {videoUrl ? (
          <VideoPlayer videoUrl={videoUrl} videoPoster={videoPoster} />
        ) : (
          <></>
        )}

        <div style={styles.rightCol}>
          <div>
            <div style={styles.amount}>${amount}</div>
            <span style={styles.balanceNote}>
              <svg
                className="w-6 h-6"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
              >
                <path
                  fill-rule="evenodd"
                  clip-rule="evenodd"
                  d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22ZM10.2426 14.4142L17.3137 7.34315L18.7279 8.75736L10.2426 17.2426L6 13L7.41421 11.5858L10.2426 14.4142Z"
                  fill="#0A7A3B"
                />
              </svg>
              added to your balance
            </span>
          </div>
          {videoUrl && (
            <div style={styles.helperText}>
              Tap play to watch a message from {gifterName}
            </div>
          )}
        </div>
      </div>

      {textMessage && <div style={styles.messageBody}>{textMessage}</div>}

      {!alreadyThanked && !sent && (
        <ThankYouForm
          gifterName={gifterName}
          submitting={submitting}
          onSubmit={handleThank}
        />
      )}

      {(alreadyThanked || sent) && (
        <div style={styles.thankedBanner}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#059669"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 6L9 17l-5-5" />
          </svg>
          <span>
            {sent
              ? 'Thank-you sent!'
              : `You thanked ${gifterName.split(' ')[0]}`}
          </span>
        </div>
      )}

      {error && (
        <div style={styles.errorBanner} onClick={() => setError(null)}>
          {error}
        </div>
      )}

      <div style={styles.divider}></div>

      <a href={getAppLink(giftId)} style={styles.secondaryBtn}>
        Open Cauze to Give &rarr;
      </a>
    </div>
  );
}

const GiftDetailModal = ({ isOpen, onToggleClose, giftId, onThanked }) => {
  const [gift, setGift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  useKeyboardEvent('Escape', () => onToggleClose(false));

  const loadGift = useCallback(() => {
    if (!isOpen || !giftId) return;

    let cancelled = false;
    setLoading(true);
    setFetchError(null);
    setGift(null);

    fetchGiftDetails(giftId)
      .then((data) => {
        if (!cancelled) {
          setGift(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error('Failed to load gift:', err);
          setFetchError("Couldn't load this gift. Try again?");
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, giftId]);

  useEffect(() => loadGift(), [loadGift]);

  return (
    <div
      className={classnames('modal gift-detail-modal', { 'is-active': isOpen })}
    >
      <div className="modal-background" onClick={() => onToggleClose(false)} />
      <div className="modal-card" style={styles.modalCard}>
        <section
          className="modal-card-body gift-detail-modal-scroll"
          style={styles.modalCardBody}
        >
          {loading && <CauzeSpinner marginTop="60px" />}

          {!loading && fetchError && (
            <div style={styles.errorWrap}>
              <div style={styles.errorIcon}>😕</div>
              <div style={styles.errorText}>{fetchError}</div>
              <button onClick={loadGift} style={styles.retryBtn}>
                Try again
              </button>
            </div>
          )}

          {!loading && gift && (
            <GiftDetailContent
              gift={gift}
              giftId={giftId}
              onClose={() => onToggleClose(false)}
              onThanked={onThanked}
            />
          )}
        </section>
      </div>
    </div>
  );
};

export default GiftDetailModal;

const FONT = "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif";

const styles = {
  modalCard: {
    width: '100%',
    maxWidth: 420,
    borderRadius: 24,
    boxShadow: '0 25px 60px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.04)',
    flex: 'unset',
  },
  modalCardBody: {
    padding: 0,
    borderRadius: 24,
    maxHeight: '90dvh',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  content: {
    padding: '16px 20px 24px',
    background: '#FFF8F2',
    fontFamily: FONT,
    position: 'relative',
  },
  closeBtn: {
    position: 'absolute',
    top: 14,
    right: 14,
    width: 32,
    height: 32,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(0, 0, 0, 0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#4f4b49',
    zIndex: 2,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: 14,
    paddingTop: 8,
    paddingRight: 36,
  },
  headerText: {
    flex: 1,
    minWidth: 0,
  },
  headerLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: '#8a8785',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  headerName: {
    fontSize: 30,
    fontWeight: 700,
    color: '#1a1a1a',
    letterSpacing: '-0.02em',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontFamily: "'Fraunces', Georgia, serif",
  },
  avatarImage: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    objectFit: 'cover',
    flexShrink: 0,
    border: '1px solid #ece8e3',
    background: '#fff',
  },
  avatarFallback: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    border: '1px solid #ece8e3',
    background: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.02em',
    color: '#b3261e',
    flexShrink: 0,
  },
  mainRow: {
    display: 'flex',
    gap: 14,
    alignItems: 'stretch',
    marginBottom: 14,
  },
  rightCol: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    flex: 1,
  },
  videoContainer: {
    width: '50%',
    minWidth: 150,
    maxWidth: 210,
    borderRadius: 18,
    overflow: 'hidden',
    background: 'linear-gradient(135deg, #2a2a2a, #1a1a1a)',
    position: 'relative',
    flexShrink: 0,
  },
  videoElement: {
    width: '100%',
    height: 'auto',
    display: 'block',
    maxHeight: 340,
    objectFit: 'contain',
  },
  playBtn: {
    width: 60,
    height: 60,
    borderRadius: '50%',
    border: 'none',
    background: 'rgba(255,255,255,0.95)',
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  videoExpandBtn: {
    position: 'absolute',
    top: 10,
    right: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 8,
    border: 'none',
    background: 'rgba(0,0,0,0.45)',
    cursor: 'pointer',
  },
  videoPlaceholder: {
    width: '50%',
    minWidth: 150,
    maxWidth: 210,
    minHeight: 260,
    borderRadius: 18,
    background: '#1f1f1f',
    color: '#f3f3f3',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 12,
    textAlign: 'center',
    fontSize: 12,
  },
  amount: {
    fontSize: 56,
    fontWeight: 600,
    color: '#1a1a1a',
    letterSpacing: '-0.04em',
    lineHeight: 1,
    fontFamily: "'Fraunces', Georgia, serif",
  },
  balanceNote: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    fontWeight: 600,
    color: '#0A7A3B',
    marginTop: 4,
  },
  helperText: {
    fontSize: 12,
    lineHeight: 1.4,
    color: '#777',
    marginBottom: 4,
  },
  messageBody: {
    background: '#fff',
    border: '1px solid #e7e2dc',
    borderRadius: 12,
    padding: '10px 12px',
    fontSize: 13,
    lineHeight: 1.45,
    color: '#6d6966',
    marginBottom: 14,
  },
  thankHeaderRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  thankSection: {
    marginTop: 2,
    marginBottom: 8,
  },
  thankTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: 0,
  },
  doneBtn: {
    border: 'none',
    background: 'transparent',
    color: '#ff6b35',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
    padding: 0,
    fontFamily: FONT,
  },
  noteInput: {
    width: '100%',
    padding: '13px 14px',
    borderRadius: 14,
    border: '1px solid #e5e5e5',
    fontSize: 15,
    fontFamily: FONT,
    resize: 'none',
    outline: 'none',
    lineHeight: 1.4,
    boxSizing: 'border-box',
    marginBottom: 12,
    minHeight: 64,
    background: '#fff',
  },
  primaryBtn: {
    width: '100%',
    padding: '15px',
    borderRadius: 999,
    border: 'none',
    background: '#ff6b35',
    color: '#fff',
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: FONT,
    boxShadow: '0 4px 16px rgba(255, 107, 53, 0.3)',
    position: 'sticky',
    bottom: 8,
    zIndex: 3,
  },
  primaryBtnDisabled: {
    opacity: 0.5,
    cursor: 'default',
  },
  thankedBanner: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    padding: '13px',
    borderRadius: 10,
    background: '#ECFDF5',
    fontSize: 13,
    fontWeight: 600,
    color: '#059669',
    fontFamily: FONT,
    marginBottom: 10,
  },
  errorBanner: {
    marginTop: 12,
    padding: '10px 14px',
    borderRadius: 10,
    background: '#FEF2F2',
    color: '#DC2626',
    fontSize: 13,
    fontWeight: 500,
    textAlign: 'center',
    cursor: 'pointer',
    fontFamily: FONT,
  },
  fold: {
    borderTop: '2px dashed rgba(255, 107, 53, 0.4)',
    margin: '20px -20px 16px',
    textAlign: 'center',
  },
  foldText: {
    display: 'inline-block',
    background: '#FFF8F2',
    color: '#ff6b35',
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    padding: '0 8px',
    transform: 'translateY(-7px)',
  },
  divider: {
    textAlign: 'center',
    margin: '2px 0 14px',
  },
  dividerText: {
    color: '#9b9895',
    fontSize: 11,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
  },
  secondaryBtn: {
    display: 'block',
    width: '100%',
    background: '#fff',
    color: '#1a1a1a',
    border: '1.5px solid #1a1a1a',
    padding: '15px',
    borderRadius: 999,
    fontFamily: FONT,
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    textAlign: 'center',
  },
  footerLink: {
    textAlign: 'center',
    marginTop: 16,
    fontSize: 12,
    color: '#777',
  },
  linkBtn: {
    border: 'none',
    background: 'transparent',
    color: '#ff6b35',
    fontWeight: 600,
    fontSize: 'inherit',
    fontFamily: 'inherit',
    cursor: 'pointer',
    padding: 0,
  },
  errorWrap: {
    padding: '40px 20px',
    textAlign: 'center',
    fontFamily: FONT,
  },
  errorIcon: {
    fontSize: 32,
    marginBottom: 12,
  },
  errorText: {
    fontSize: 14,
    color: '#4A4A57',
    marginBottom: 16,
    lineHeight: 1.5,
  },
  retryBtn: {
    padding: '10px 24px',
    borderRadius: 10,
    border: '1.5px solid #EBEBEF',
    background: '#fff',
    color: '#0F0F14',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: FONT,
  },
};
